import { useMemo, useState } from "react";
import { SlideOver } from "../components/chrome/Modal";
import { Icon } from "../components/Icon";
import { useHelp } from "./HelpController";
import { searchGuides } from "./search";
import { FIRST_RUN_TOUR_ID } from "./tourState";
import type { HelpGuide } from "./types";
import { useWelcomeOpener } from "../workspace/welcomeSlot";

/**
 * The little tag on a result row. A dial-kind guide says which kind it's for — from the dashboard both
 * status guides are listed, and without this you'd have no way to tell which one is yours.
 */
function scopeBadge(guide: HelpGuide): { label: string; title: string } | null {
  if (guide.dialKind === "simple") {
    return { label: "For simple dials", title: "This guide is for dials scored on the action point itself" };
  }
  if (guide.dialKind === "itemised") {
    return { label: "For itemised dials", title: "This guide is for dials scored item by item" };
  }
  if (guide.context === "dial") {
    return { label: "In a dial", title: "Open a dial to use this guide" };
  }
  return null;
}

/**
 * The searchable "how do I…" launcher. Type a question, pick a guide, and the interactive walkthrough
 * starts. Also links out to the existing Help videos, so this complements them rather than replacing them.
 */
export function HelpLauncher({ onClose, onOpenVideos }: { onClose: () => void; onOpenVideos: () => void }) {
  const { start, dialKind } = useHelp();
  // KEY-814: the open dial's welcome page, when it has one this person may see.
  const welcome = useWelcomeOpener();
  const [query, setQuery] = useState("");
  const results = useMemo(() => searchGuides(query, dialKind), [query, dialKind]);

  const run = (guideId: string) => {
    start(guideId);
    onClose();
  };

  return (
    <SlideOver title="Help — how do I…" onClose={onClose}>
      {welcome && (
        <button
          onClick={() => {
            onClose();
            welcome.open();
          }}
          className="kd-row-hover"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            textAlign: "left",
            padding: 12,
            marginBottom: 14,
            borderRadius: "var(--radius)",
            border: "var(--border-w) solid var(--accent-border)",
            background: "var(--accent-soft)",
            cursor: "pointer",
            font: "inherit",
          }}
        >
          <Icon name="target" size={17} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontWeight: 700, fontSize: 14, color: "var(--accent-text)" }}>Getting started with this dial</span>
            <span style={{ display: "block", fontSize: 12.5, color: "var(--text-muted)", marginTop: 1 }}>What {welcome.dialName} needs from you to be useful</span>
          </span>
        </button>
      )}
      <div style={{ position: "relative", marginBottom: 14 }}>
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-faint)", pointerEvents: "none" }}>
          <Icon name="search" size={16} />
        </span>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. how do I add a file?"
          aria-label="Search help"
          style={{
            width: "100%",
            font: "inherit",
            fontSize: 14.5,
            padding: "11px 12px 11px 36px",
            borderRadius: "var(--radius-sm)",
            border: "var(--border-w) solid var(--border)",
            background: "var(--surface-2)",
            color: "var(--text)",
          }}
        />
      </div>

      {results.length === 0 ? (
        <p style={{ fontSize: 13.5, color: "var(--text-faint)", lineHeight: 1.5 }}>
          No guides match “{query}”. Try “add a file”, “change status”, “send a message”, or “members”.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {results.map((g) => {
            const badge = scopeBadge(g);
            return (
            <button
              key={g.id}
              onClick={() => run(g.id)}
              className="kd-row-hover"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                textAlign: "left",
                padding: 12,
                borderRadius: "var(--radius)",
                border: "var(--border-w) solid var(--border)",
                background: "var(--surface-2)",
                cursor: "pointer",
                font: "inherit",
              }}
            >
              <span
                style={{
                  width: 34,
                  height: 34,
                  flex: "none",
                  borderRadius: 8,
                  background: "var(--accent-soft)",
                  color: "var(--accent-text)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name="help" size={17} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{g.title}</span>
                <span style={{ display: "block", fontSize: 12.5, color: "var(--text-faint)", marginTop: 1 }}>{g.summary}</span>
              </span>
              {badge && (
                <span
                  style={{
                    flex: "none",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text-muted)",
                    background: "var(--surface-inset)",
                    borderRadius: 99,
                    padding: "3px 8px",
                  }}
                  title={badge.title}
                >
                  {badge.label}
                </span>
              )}
            </button>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 18, paddingTop: 14, borderTop: "var(--border-w) solid var(--border)" }}>
        {/* Pinned rather than left to the search results above, because this is the answer to "can I have
            that walkthrough again?" (KEY-535) — and somebody who pressed ✕ on it has no reason to know it
            was called a tour, so they can't search for it. The tour's own last-but-one step promises it is
            at the bottom of this drawer, so it has to be here whatever the query. */}
        <button
          onClick={() => {
            onClose();
            start(FIRST_RUN_TOUR_ID);
          }}
          className="kd-row-hover"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            width: "100%",
            padding: 10,
            borderRadius: "var(--radius-sm)",
            border: "none",
            background: "none",
            cursor: "pointer",
            font: "inherit",
            fontSize: 13.5,
            fontWeight: 600,
            color: "var(--text-muted)",
          }}
        >
          <Icon name="target" size={16} />
          New here? Take the tour of a dial again
        </button>
        <button
          onClick={() => {
            onClose();
            onOpenVideos();
          }}
          className="kd-row-hover"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            width: "100%",
            padding: 10,
            borderRadius: "var(--radius-sm)",
            border: "none",
            background: "none",
            cursor: "pointer",
            font: "inherit",
            fontSize: 13.5,
            fontWeight: 600,
            color: "var(--text-muted)",
          }}
        >
          <Icon name="help" size={16} />
          Prefer to watch? Open Help videos
        </button>
      </div>
    </SlideOver>
  );
}
